import { makeStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import Tooltip from '@material-ui/core/Tooltip'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineFileExcel, AiOutlineFilePdf } from 'react-icons/ai'

import { PolicyAmount, Provider } from 'src/api/gen/api'
import { CurrencyKind } from 'src/api/types/units'
import { TABLE_ROW_HEIGHT } from 'src/components/Table'
import { useCurrencyFormatter } from 'src/layout/AgencySettings'
import { colors } from 'src/theme'

import PenaltySchemesTable from './PenaltySchemesTable'

import { IPenaltyScheme } from '.'

const FONT_SIZE = '18px'

export interface IProviderInfo {
  amount: number
  data: PolicyAmount[]
  offenses_count: number
  provider: Provider
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    amount: {
      fontWeight: 'bold',
      fontSize: FONT_SIZE,
      margin: theme.spacing(),
    },
    amountSubtitle: {
      color: colors.grey,
      FONT_SIZE,
      margin: theme.spacing(),
    },
    providerName: {
      color: theme.palette.primary.main,
      FONT_SIZE,
      fontWeight: 'bold',
      margin: theme.spacing(),
    },
    media: {
      textAlign: 'center',
      height: 100,
      width: 100,
      margin: 20,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    tooltip: {
      fontSize: 13,
      margin: theme.spacing(2),
    },
    expandIcon: {
      fontSize: 30,
    },
    exportIcon: {
      fontSize: 30,
      cursor: 'pointer',
      color: '#192233',
    },
    exportContainer: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      marginLeft: 'auto',
      marginRight: theme.spacing(2),
    },
  })
})

interface IProps {
  info: IProviderInfo
  onClickExport: (event: React.MouseEvent<HTMLElement>) => void
  onClickExportPdf: (event: React.MouseEvent<HTMLElement>) => void
  penaltyScheme: IPenaltyScheme
}

const PenaltySchemesAccordion = function (props: IProps) {
  const classes = useStyles()
  const { t } = useTranslation()
  const currencyFormatter = useCurrencyFormatter(CurrencyKind.Global)

  const onExportClick = function (event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation()
    return props.onClickExport(event)
  }

  const onExportClickPdf = function (event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation()
    return props.onClickExportPdf(event)
  }

  // Let's make sure the included table has all the size needed for its lines and header
  const accordionDetailsHeight = TABLE_ROW_HEIGHT * (2 + (props.info.data?.length ?? 0))

  return (
    // Accordion needs to be controlled to be able to memoized its content
    <Accordion>
      <AccordionSummary sx={{ px: 5 }} expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}>
        <div
          style={{
            backgroundImage: `url(${props.info.provider.logo ?? '/img/img-placeholder.svg'})`,
          }}
          className={classes.media}
        />
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
            alignContent: 'center',
          }}
        >
          <div className={classes.providerName}>{props.info.provider.name}</div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'left' }}>
            <div className={classes.amount}>{currencyFormatter(props.info.amount)}</div>
            <div className={classes.amountSubtitle}>
              {t('subtitles.filtered_selection.penalty_scheme', { policies_count: props.info.data.length })}
            </div>
          </div>
        </div>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box>
            <Tooltip title={t('common.export') as string} placement="bottom" classes={{ tooltip: classes.tooltip }}>
              <IconButton sx={{ fontSize: 25 }} onClick={onExportClick}>
                <AiOutlineFileExcel />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title={t('common.export_pdf') as string} placement="bottom" classes={{ tooltip: classes.tooltip }}>
              <IconButton sx={{ fontSize: 25 }} onClick={onExportClickPdf}>
                <AiOutlineFilePdf />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails style={{ height: accordionDetailsHeight }}>
        <PenaltySchemesTable
          data={props.info.data}
          totalCount={props.info.data.length}
          providerId={props.info.provider.id}
          penaltyScheme={props.penaltyScheme}
        />
      </AccordionDetails>
    </Accordion>
  )
}

export default PenaltySchemesAccordion
